const API_URL = import.meta.env.VITE_API_URL || '';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  created_at: string;
}

export interface Note {
  id: string;
  user_id: string;
  title: string;
  content: string;
  summary: string | null;
  embedding: string | null;
  created_at: string;
  updated_at: string;
}

export interface NoteWithAttachments extends Note {
  attachments: Attachment[];
}

export interface Attachment {
  id: string;
  note_id: string;
  filename: string;
  content_type: string;
  size: number;
  r2_key: string;
  created_at: string;
}

class ApiClient {
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    customHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
  }

  // Auth
  async register(email: string, name: string, password: string): Promise<{ user: User; token: string }> {
    const response = await this.request<{ user: User; token: string }>('POST', '/api/auth/register', {
      email,
      name,
      password,
    });
    if (response.data) {
      localStorage.setItem('token', response.data.token);
      return response.data;
    }
    throw new Error('Registration failed');
  }

  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    const response = await this.request<{ user: User; token: string }>('POST', '/api/auth/login', {
      email,
      password,
    });
    if (response.data) {
      localStorage.setItem('token', response.data.token);
      return response.data;
    }
    throw new Error('Login failed');
  }

  async getMe(): Promise<User> {
    const response = await this.request<User>('GET', '/api/auth/me');
    if (response.data) return response.data;
    throw new Error('Failed to get user');
  }

  logout(): void {
    localStorage.removeItem('token');
  }

  // Notes
  async getNotes(): Promise<Note[]> {
    const response = await this.request<Note[]>('GET', '/api/notes');
    return response.data || [];
  }

  async searchNotes(query: string, semantic = false): Promise<Note[]> {
    const params = new URLSearchParams({ q: query });
    if (semantic) params.append('semantic', 'true');
    const response = await this.request<Note[]>('GET', `/api/notes/search?${params}`);
    return response.data || [];
  }

  async getNote(id: string): Promise<NoteWithAttachments> {
    const response = await this.request<NoteWithAttachments>('GET', `/api/notes/${id}`);
    if (response.data) return response.data;
    throw new Error('Note not found');
  }

  async createNote(
    title: string, 
    content: string, 
    generateSummary = false
  ): Promise<Note> {
    const response = await this.request<Note>('POST', '/api/notes', {
      title,
      content,
      generateSummary,
    });
    if (response.data) return response.data;
    throw new Error('Failed to create note');
  }

  async updateNote(
    id: string,
    updates: { title?: string; content?: string },
    generateSummary = false
  ): Promise<Note> {
    const response = await this.request<Note>('PATCH', `/api/notes/${id}`, {
      ...updates,
      generateSummary,
    });
    if (response.data) return response.data;
    throw new Error('Failed to update note');
  }

  async deleteNote(id: string): Promise<void> {
    await this.request('DELETE', `/api/notes/${id}`);
  }

  async askAI(id: string, question: string): Promise<string> {
    const response = await this.request<{ answer: string }>('POST', `/api/notes/${id}/ask`, { question });
    return response.data?.answer || '';
  }

  // Uploads
  async uploadFile(noteId: string, file: File): Promise<Attachment> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/uploads/${noteId}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Upload failed');
    }
    return data.data;
  }

  getFileUrl(attachmentId: string): string {
    const token = this.getToken();
    if (token) {
      return `${API_URL}/api/uploads/file/${attachmentId}?token=${encodeURIComponent(token)}`;
    }
    return `${API_URL}/api/uploads/file/${attachmentId}`;
  }

  async deleteFile(attachmentId: string): Promise<void> {
    await this.request('DELETE', `/api/uploads/${attachmentId}`);
  }
}

export const api = new ApiClient();
